import type {
  TimesheetRecord,
  AMEntry,
  AccountOwnerOverride,
  ClientInvoiceByPeriod,
  RateConfig,
} from '../hooks/useAccountOwnerData';

// ─── Types ────────────────────────────────────────────────────────────────────
export type PeriodType = 'fy_india' | 'calendar';

export interface ResourceEntry {
  resourceId: number;
  resourceName: string;
  billable: number;
  total: number;
  nonBillable: number;
  late: number;
  pending: number;
  revenue: number;
  /** DRC: billable hrs × effective cost rate after misc. ((raw − misc) × hrs). */
  directResourceCost: number;
  /** Cost at list rate: billable hrs × raw grade × tech $/h (before misc.). */
  cost: number;
  profit: number;
}

export interface ProjectEntry {
  projectId: number;
  projectName: string;
  clientName: string;
  projectManager: string;
  resources: Record<string, ResourceEntry>;
  billable: number;
  total: number;
  revenue: number;
  directResourceCost: number;
  cost: number;
  profit: number;
}

export interface ClientEntry {
  clientKey: string;
  clientName: string;
  clientId: string;
  projects: Record<string, ProjectEntry>;
  billable: number;
  total: number;
  revenue: number;
  directResourceCost: number;
  cost: number;
  profit: number;
}

export interface AMDrillEntry {
  amId: number;
  amName: string;
  clients: Record<string, ClientEntry>;
  billable: number;
  total: number;
  revenue: number;
  directResourceCost: number;
  cost: number;
  profit: number;
  utilization: number;
  projectCount: number;
  resourceCount: number;
}

export interface KPISummary {
  totalRevenue: number;
  totalCost: number;
  totalProfit: number;
  totalBillable: number;
  totalHours: number;
  avgMarginPct: number | null;
  effectiveBillingRate: number | null;
  uniqueClients: number;
  uniqueProjects: number;
  uniqueResources: number;
  topAmByRevenue: string;
  amCount: number;
}

export interface AMChartEntry {
  name: string;
  fullName?: string;
  billable: number;
  revenue: number;
  cost: number;
  profit: number;
  marginPct: number | null;
  utilization: number;
  projects: number;
  resources: number;
}

// ─── Formatters ───────────────────────────────────────────────────────────────
export function fmtUSD(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-$' : '$';
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${Math.round(abs).toLocaleString('en-US')}`;
}

/** Full-precision USD for tooltips (always 2 decimal places). */
export function fmtUSDExact(n: number): string {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  return `${sign}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function fmtHours(n: number): string {
  return `${Math.round(n).toLocaleString('en-US')}h`;
}

export function fmtPct(n: number | null, places = 1): string {
  if (n == null || isNaN(n)) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(places)}%`;
}

export function fmtRate(n: number): string {
  return `$${n.toFixed(2)}/hr`;
}

// ─── Rate Config ──────────────────────────────────────────────────────────────
const ORG_DEFAULT_RATE = 20;

export function getRateForPeriod(config: RateConfig | null, year: number, month: number): number {
  if (!config) return ORG_DEFAULT_RATE;
  const overrides = config.overrides || [];
  let yearMonthMatch: number | null = null;
  let yearMatch: number | null = null;
  for (const o of overrides) {
    if (o.year !== year) continue;
    if (o.month != null && o.month === month) yearMonthMatch = o.rate;
    else if (o.month == null) yearMatch = o.rate;
  }
  return yearMonthMatch ?? yearMatch ?? config.default_rate_per_hour ?? ORG_DEFAULT_RATE;
}

export function getResourceCostRate(
  r: TimesheetRecord,
  gradeTechRates: Record<string, number>,
  rateConfig: RateConfig | null
): number {
  const grade = String(r.resource_grade ?? '').trim();
  const tech = String(r.resource_technology ?? '').trim();
  const key = `${grade}|${tech}`;
  if (grade && tech && gradeTechRates[key] != null) return Number(gradeTechRates[key]);
  return getRateForPeriod(rateConfig, r.year, r.month);
}

/**
 * Global miscellaneous direct resource cost ($/h), subtracted from the raw grade/tech (or fallback) cost rate.
 * Effective cost rate = max(0, rawCostRate − miscExpensePerHour); final cost = effective × billable hours.
 */
export function effectiveCostRateAfterMiscExpense(
  rawCostRate: number,
  miscExpensePerHour: number
): number {
  const misc = Number.isFinite(miscExpensePerHour) ? Math.max(0, miscExpensePerHour) : 0;
  const raw = Number.isFinite(rawCostRate) ? rawCostRate : 0;
  return Math.max(0, raw - misc);
}

/** Indian FY start years present in invoice month keys (YYYY-MM). */
function indianFyStartsFromByMonthKeys(byMonth: Record<string, unknown>): number[] {
  const set = new Set<number>();
  Object.keys(byMonth || {}).forEach((ym) => {
    const [yp, mp] = ym.split('-');
    const y = parseInt(yp, 10);
    const m = parseInt(mp, 10);
    if (Number.isNaN(y) || Number.isNaN(m)) return;
    const fyStart = m >= 4 ? y : y - 1;
    set.add(fyStart);
  });
  return [...set].sort((a, b) => a - b);
}

/** Calendar years present in invoice JSON (by_year keys, else by_month). */
function calendarYearsFromInvoiceData(
  byYear: Record<string, unknown>,
  byMonth: Record<string, unknown>
): string[] {
  const fromYear = Object.keys(byYear || {}).filter((k) => /^\d{4}$/.test(k));
  if (fromYear.length > 0) return [...new Set(fromYear)].sort();
  const ys = new Set<string>();
  Object.keys(byMonth || {}).forEach((ym) => {
    const y = ym.split('-')[0];
    if (y && /^\d{4}$/.test(y)) ys.add(y);
  });
  return [...ys].sort();
}

function matchesIndianFyQuarter(m: number, fyQuarter: string): boolean {
  if (fyQuarter === 'all') return true;
  if (fyQuarter === 'Q1') return m >= 4 && m <= 6;
  if (fyQuarter === 'Q2') return m >= 7 && m <= 9;
  if (fyQuarter === 'Q3') return m >= 10 && m <= 12;
  if (fyQuarter === 'Q4') return m >= 1 && m <= 3;
  return true;
}

// ─── Period Filtering ─────────────────────────────────────────────────────────
export function applyDateFilter<T extends TimesheetRecord>(
  records: T[],
  periodType: PeriodType,
  fyYears: string | string[],
  fyQuarter: string,
  tsYears: string | string[],
  tsMonth: string
): T[] {
  // Derive arrays (filter out 'all' sentinel for backwards compat)
  const fyArr = Array.isArray(fyYears)
    ? fyYears.filter(y => y !== 'all')
    : (fyYears !== 'all' ? [fyYears] : []);
  const tsArr = Array.isArray(tsYears)
    ? tsYears.filter(y => y !== 'all')
    : (tsYears !== 'all' ? [tsYears] : []);

  if (periodType === 'calendar') {
    let out = records;
    if (tsArr.length > 0) {
      const yNums = tsArr.map(Number);
      out = out.filter(r => yNums.includes(Number(r.year)));
    }
    if (tsMonth !== 'all') {
      const m = Number(tsMonth);
      out = out.filter(r => Number(r.month) === m);
    }
    return out;
  }
  // fy_india mode
  if (fyArr.length > 0) {
    const fyNums = fyArr.map(Number);
    return records.filter(r => {
      const y = Number(r.year);
      const m = Number(r.month);
      const inFy = fyNums.some(fyStart => {
        const fyEnd = fyStart + 1;
        return (m >= 4 && y === fyStart) || (m <= 3 && y === fyEnd);
      });
      if (!inFy) return false;
      return matchesIndianFyQuarter(m, fyQuarter);
    });
  }
  // All FY years selected (empty): still honor quarter if not "all"
  if (fyQuarter !== 'all') {
    return records.filter(r => matchesIndianFyQuarter(Number(r.month), fyQuarter));
  }
  return records;
}

// ─── Account Owner Overrides ──────────────────────────────────────────────────
export function buildOverrideMap(
  overrides: AccountOwnerOverride[],
  amManifest: AMEntry[]
): Record<string, { amId: number; amName: string }> {
  const nameToAm: Record<string, { amId: number; amName: string }> = {};
  amManifest.forEach(am => {
    nameToAm[String(am.name).trim()] = { amId: Number(am.id), amName: String(am.name).trim() };
  });
  const map: Record<string, { amId: number; amName: string }> = {};
  overrides.forEach(o => {
    const pname = String(o.projectName ?? '').trim();
    const target = o.accountOwnerName ? nameToAm[String(o.accountOwnerName).trim()] : null;
    if (pname && target) map[pname] = target;
  });
  return map;
}

export type EffectiveRecord = TimesheetRecord & {
  effective_account_manager_id: number;
  effective_account_manager_name: string;
};

export function applyOverrides(
  records: TimesheetRecord[],
  overrideMap: Record<string, { amId: number; amName: string }>
): EffectiveRecord[] {
  return records.map(r => {
    const key = String(r.project_name ?? '').trim();
    const ov = overrideMap[key];
    return {
      ...r,
      effective_account_manager_id: ov?.amId ?? r.account_manager_id,
      effective_account_manager_name: ov?.amName ?? (r.account_manager || `AM ${r.account_manager_id}`),
    };
  });
}

// ─── Period Revenue ───────────────────────────────────────────────────────────
export function computePeriodRevenueByClient(
  cibp: ClientInvoiceByPeriod | null,
  periodType: PeriodType,
  fyYears: string | string[],
  fyQuarter: string,
  tsYears: string | string[],
  tsMonth: string,
  excludeTax: boolean
): Record<string, number> {
  if (!cibp?.by_year_month) return {};
  const byMonth = cibp.by_year_month;
  const byYear = cibp.by_year || {};
  const raw: Record<string, number> = {};

  // Derive arrays (filter out 'all' sentinel for backwards compat)
  const fyArr = Array.isArray(fyYears)
    ? fyYears.filter(y => y !== 'all')
    : (fyYears !== 'all' ? [fyYears] : []);
  const tsArr = Array.isArray(tsYears)
    ? tsYears.filter(y => y !== 'all')
    : (tsYears !== 'all' ? [tsYears] : []);

  const add = (cid: string, amt: number) => {
    raw[cid] = (raw[cid] || 0) + (Number(amt) || 0);
  };

  const fyYearsForInvoice =
    periodType === 'fy_india'
      ? (fyArr.length > 0
          ? fyArr.map((y) => parseInt(y, 10)).filter((n) => !Number.isNaN(n))
          : indianFyStartsFromByMonthKeys(byMonth))
      : [];

  if (periodType === 'fy_india' && fyYearsForInvoice.length > 0) {
    for (const y of fyYearsForInvoice) {
      if (Number.isNaN(y)) continue;
      let months: string[] = [];
      if (fyQuarter === 'Q1') months = [`${y}-04`, `${y}-05`, `${y}-06`];
      else if (fyQuarter === 'Q2') months = [`${y}-07`, `${y}-08`, `${y}-09`];
      else if (fyQuarter === 'Q3') months = [`${y}-10`, `${y}-11`, `${y}-12`];
      else if (fyQuarter === 'Q4') months = [`${y + 1}-01`, `${y + 1}-02`, `${y + 1}-03`];
      else months = [
        `${y}-04`, `${y}-05`, `${y}-06`,
        `${y}-07`, `${y}-08`, `${y}-09`,
        `${y}-10`, `${y}-11`, `${y}-12`,
        `${y + 1}-01`, `${y + 1}-02`, `${y + 1}-03`,
      ];
      months.forEach(ym => {
        const period = byMonth[ym];
        if (period?.clients) {
          Object.entries(period.clients).forEach(([cid, amt]) => add(cid, amt as number));
        }
      });
    }
  } else if (periodType === 'calendar') {
    const calendarYearsForInvoice =
      tsArr.length > 0 ? tsArr.map(String) : calendarYearsFromInvoiceData(byYear, byMonth);
    for (const tsYear of calendarYearsForInvoice) {
      if (tsMonth !== 'all') {
        const ym = `${tsYear}-${String(tsMonth).padStart(2, '0')}`;
        const period = byMonth[ym];
        if (period?.clients) {
          Object.entries(period.clients).forEach(([cid, amt]) => add(cid, amt as number));
        }
      } else {
        const period = byYear[String(tsYear)];
        if (period?.clients) {
          Object.entries(period.clients).forEach(([cid, amt]) => add(cid, amt as number));
        }
      }
    }
  }

  if (!excludeTax || !cibp.client_tax_pct) return raw;
  const taxPct = cibp.client_tax_pct;
  const out: Record<string, number> = {};
  Object.keys(raw).forEach(key => {
    const tp = taxPct[key];
    out[key] = (tp != null && Number(tp) > 0) ? raw[key] / (1 + Number(tp) / 100) : raw[key];
  });
  return out;
}

// ─── Compute effective billing rate per client ────────────────────────────────
export function computeEffectiveRateByClient(
  records: EffectiveRecord[],
  projectToClient: Record<string, { client_id: string; client_name: string }>,
  periodRevenueByClient: Record<string, number>
): Record<string, number> {
  const totalBillable: Record<string, number> = {};
  records.forEach(r => {
    const ptc = projectToClient[String(r.project_id)];
    if (!ptc?.client_id) return;
    const key = String(ptc.client_id);
    const bh = parseFloat(String(r.billable_hours)) || 0;
    totalBillable[key] = (totalBillable[key] || 0) + bh;
  });
  const result: Record<string, number> = {};
  Object.keys(totalBillable).forEach(key => {
    const b = totalBillable[key];
    const rev = periodRevenueByClient[key];
    if (b > 0 && rev != null && Number(rev) >= 0) result[key] = Number(rev) / b;
  });
  return result;
}

// ─── Build drill-down hierarchy ───────────────────────────────────────────────
export function buildDrillDown(
  records: EffectiveRecord[],
  projectToClient: Record<string, { client_id: string; client_name: string }>,
  clientBillingRateByClientId: Record<string, number>,
  effectiveRateByClient: Record<string, number>,
  gradeTechRates: Record<string, number>,
  rateConfig: RateConfig | null,
  miscExpensePerHour = 0
): Record<number, AMDrillEntry> {
  const parseNum = (v: unknown) => (typeof v === 'string' ? parseFloat(v) : Number(v)) || 0;
  const byAm: Record<number, AMDrillEntry> = {};

  records.forEach(r => {
    const amId = r.effective_account_manager_id;
    const amName = r.effective_account_manager_name || `AM ${amId}`;
    const pid = r.project_id;
    const rid = r.resource_id;
    const ptc = projectToClient[String(pid)];
    const rawFromMap = (ptc?.client_name || '').trim();
    const clientName = rawFromMap || '—';
    const cid = ptc?.client_id ? parseInt(ptc.client_id, 10) : NaN;
    const clientKey = !isNaN(cid) ? String(cid) : (clientName || '—');
    const pm = String(r.project_manager ?? '').trim() || '—';

    const clientRate =
      effectiveRateByClient[clientKey] != null
        ? effectiveRateByClient[clientKey]
        : (!isNaN(cid) && clientBillingRateByClientId[String(cid)] != null
            ? Number(clientBillingRateByClientId[String(cid)])
            : 0);
    const rawCostRate = getResourceCostRate(r, gradeTechRates, rateConfig);
    const costRate = effectiveCostRateAfterMiscExpense(rawCostRate, miscExpensePerHour);

    const bh = parseNum(r.billable_hours);
    const th = parseNum(r.total_hours);
    const nb = parseNum(r.non_billable_hours);
    const late = parseNum(r.late_entries_count);
    const pend = parseNum(r.pending_signoff_entries);
    const revenue = bh * clientRate;
    const cost = bh * rawCostRate;
    const directResourceCost = bh * costRate;
    const profit = revenue - cost;

    // AM level
    if (!byAm[amId]) {
      byAm[amId] = {
        amId,
        amName,
        clients: {},
        billable: 0,
        total: 0,
        revenue: 0,
        directResourceCost: 0,
        cost: 0,
        profit: 0,
        utilization: 0,
        projectCount: 0,
        resourceCount: 0,
      };
    }
    const amEntry = byAm[amId];
    amEntry.billable += bh;
    amEntry.total += th;
    amEntry.revenue += revenue;
    amEntry.cost += cost;
    amEntry.directResourceCost += directResourceCost;
    amEntry.profit += profit;

    // Client level
    if (!amEntry.clients[clientKey]) {
      amEntry.clients[clientKey] = {
        clientKey,
        clientName,
        clientId: String(cid),
        projects: {},
        billable: 0,
        total: 0,
        revenue: 0,
        directResourceCost: 0,
        cost: 0,
        profit: 0,
      };
    }
    const clientEntry = amEntry.clients[clientKey];
    if (rawFromMap && clientEntry.clientName === '—') {
      clientEntry.clientName = rawFromMap;
    }
    clientEntry.billable += bh;
    clientEntry.total += th;
    clientEntry.revenue += revenue;
    clientEntry.cost += cost;
    clientEntry.directResourceCost += directResourceCost;
    clientEntry.profit += profit;

    // Project level
    if (!clientEntry.projects[String(pid)]) {
      clientEntry.projects[String(pid)] = {
        projectId: pid,
        projectName: r.project_name || `Project ${pid}`,
        clientName,
        projectManager: pm,
        resources: {},
        billable: 0,
        total: 0,
        revenue: 0,
        directResourceCost: 0,
        cost: 0,
        profit: 0,
      };
    }
    const projEntry = clientEntry.projects[String(pid)];
    if (rawFromMap && projEntry.clientName === '—') {
      projEntry.clientName = rawFromMap;
    }
    projEntry.billable += bh;
    projEntry.total += th;
    projEntry.revenue += revenue;
    projEntry.cost += cost;
    projEntry.directResourceCost += directResourceCost;
    projEntry.profit += profit;

    // Resource level
    if (!projEntry.resources[String(rid)]) {
      projEntry.resources[String(rid)] = {
        resourceId: rid,
        resourceName: r.resource_name || `Resource ${rid}`,
        billable: 0,
        total: 0,
        nonBillable: 0,
        late: 0,
        pending: 0,
        revenue: 0,
        directResourceCost: 0,
        cost: 0,
        profit: 0,
      };
    }
    const resEntry = projEntry.resources[String(rid)];
    resEntry.billable += bh;
    resEntry.total += th;
    resEntry.nonBillable += nb;
    resEntry.late += late;
    resEntry.pending += pend;
    resEntry.revenue += revenue;
    resEntry.cost += cost;
    resEntry.directResourceCost += directResourceCost;
    resEntry.profit += profit;
  });

  // Finalize AM level stats
  Object.values(byAm).forEach(am => {
    const projSet = new Set<string>();
    const resSet = new Set<string>();
    Object.values(am.clients).forEach(cl => {
      Object.values(cl.projects).forEach(proj => {
        projSet.add(String(proj.projectId));
        Object.keys(proj.resources).forEach(rk => resSet.add(rk));
      });
    });
    am.projectCount = projSet.size;
    am.resourceCount = resSet.size;
    am.utilization = am.total > 0 ? Math.min(100, (am.billable / am.total) * 100) : 0;
  });

  return byAm;
}

// ─── Compute KPIs ─────────────────────────────────────────────────────────────
export function computeKPIs(drillDown: Record<number, AMDrillEntry>): KPISummary {
  const ams = Object.values(drillDown);
  const totalRevenue = ams.reduce((s, a) => s + a.revenue, 0);
  /** Sum of Cost (raw grade × tech × hrs); profit/margin use this basis. */
  const totalCost = ams.reduce((s, a) => s + a.cost, 0);
  const totalProfit = ams.reduce((s, a) => s + a.profit, 0);
  const totalBillable = ams.reduce((s, a) => s + a.billable, 0);
  const totalHours = ams.reduce((s, a) => s + a.total, 0);
  const clientSet = new Set<string>();
  const projSet = new Set<string>();
  const resSet = new Set<string>();
  ams.forEach(am => {
    Object.entries(am.clients).forEach(([ck, cl]) => {
      clientSet.add(ck);
      Object.values(cl.projects).forEach(proj => {
        projSet.add(String(proj.projectId));
        Object.keys(proj.resources).forEach(rk => resSet.add(rk));
      });
    });
  });
  const sorted = [...ams].sort((a, b) => b.revenue - a.revenue);
  const topAm = sorted[0];
  return {
    totalRevenue,
    totalCost,
    totalProfit,
    totalBillable,
    totalHours,
    avgMarginPct: totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : null,
    effectiveBillingRate: totalBillable > 0 ? totalRevenue / totalBillable : null,
    uniqueClients: clientSet.size,
    uniqueProjects: projSet.size,
    uniqueResources: resSet.size,
    topAmByRevenue: topAm?.amName ?? '—',
    amCount: ams.length,
  };
}

// ─── Chart data ───────────────────────────────────────────────────────────────
export function buildAMChartData(drillDown: Record<number, AMDrillEntry>): AMChartEntry[] {
  return Object.values(drillDown)
    .map(am => ({
      name: am.amName.split(' ')[0], // first name for chart brevity
      fullName: am.amName,
      billable: Math.round(am.billable),
      revenue: Math.round(am.revenue),
      cost: Math.round(am.cost),
      profit: Math.round(am.profit),
      marginPct: am.revenue > 0 ? Math.round((am.profit / am.revenue) * 1000) / 10 : null,
      utilization: Math.round(am.utilization * 10) / 10,
      projects: am.projectCount,
      resources: am.resourceCount,
    } as AMChartEntry & { fullName: string }))
    .sort((a, b) => b.revenue - a.revenue);
}

// ─── FY option helpers ────────────────────────────────────────────────────────
export function deriveFYOptions(records: TimesheetRecord[]): string[] {
  const fySet = new Set<string>();
  records.forEach(r => {
    const y = Number(r.year);
    const m = Number(r.month);
    const fy = m >= 4 ? String(y) : String(y - 1);
    fySet.add(fy);
  });
  return Array.from(fySet).sort().reverse();
}

export function deriveCalendarYears(records: TimesheetRecord[]): string[] {
  const ySet = new Set<string>();
  records.forEach(r => ySet.add(String(r.year)));
  return Array.from(ySet).sort().reverse();
}

// ─── Monthly Analytics (for time-series charts) ──────────────────────────────
export interface MonthlyAggMetrics {
  ym: string;              // "2025-04"
  label: string;           // "Apr '25"
  revenue: number;
  cost: number;
  profit: number;
  marginPct: number | null;
  billableHours: number;
  effBillingRate: number | null;
}

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export function buildMonthlyMetrics(
  effectiveRecords: EffectiveRecord[],
  fyYear: string,  // e.g. "2025" → FY 2025-26 (Apr 2025 – Mar 2026)
  gradeTechRates: Record<string, number>,
  rateConfig: RateConfig | null,
  clientBillingRateByClientId: Record<string, number>,
  projectToClient: Record<string, { client_id: number | string; client_name: string }>,
  effectiveRateByClient: Record<string, number> = {}
): MonthlyAggMetrics[] {
  const fyStart = parseInt(fyYear, 10);
  if (isNaN(fyStart)) return [];
  const fyEnd = fyStart + 1;

  // All 12 months of the Indian FY in order: Apr → Mar
  const fyMonths = [
    `${fyStart}-04`, `${fyStart}-05`, `${fyStart}-06`,
    `${fyStart}-07`, `${fyStart}-08`, `${fyStart}-09`,
    `${fyStart}-10`, `${fyStart}-11`, `${fyStart}-12`,
    `${fyEnd}-01`,   `${fyEnd}-02`,   `${fyEnd}-03`,
  ];

  const map = new Map<string, { revenue: number; cost: number; billableHours: number }>();
  fyMonths.forEach(ym => map.set(ym, { revenue: 0, cost: 0, billableHours: 0 }));

  effectiveRecords.forEach(r => {
    const y = Number(r.year);
    const m = Number(r.month);
    // Only include records within this FY
    const inFY = (m >= 4 && y === fyStart) || (m <= 3 && y === fyEnd);
    if (!inFY) return;

    const ym = `${y}-${String(m).padStart(2, '0')}`;
    const entry = map.get(ym);
    if (!entry) return;

    const bh = parseFloat(r.billable_hours) || 0;
    // Use project_id (not project_name) to look up client
    const ptc = projectToClient[String(r.project_id)];
    const clientId = ptc?.client_id;
    const clientKey = clientId != null ? String(clientId) : null;
    // Prefer effectiveRateByClient (derived from actual invoices), fall back to config rate
    const clientRate = clientKey != null
      ? (effectiveRateByClient[clientKey] != null
          ? effectiveRateByClient[clientKey]
          : (clientBillingRateByClientId[clientKey] != null ? Number(clientBillingRateByClientId[clientKey]) : 0))
      : 0;
    const rawCostRate = getResourceCostRate(r, gradeTechRates, rateConfig);

    entry.billableHours += bh;
    entry.revenue += bh * clientRate;
    entry.cost += bh * rawCostRate;
  });

  return fyMonths.map(ym => {
    const [y, mo] = ym.split('-').map(Number);
    const entry = map.get(ym)!;
    const profit = entry.revenue - entry.cost;
    const marginPct = entry.revenue > 0 ? (profit / entry.revenue) * 100 : null;
    const effBillingRate = entry.billableHours > 0 ? entry.revenue / entry.billableHours : null;
    return {
      ym,
      label: `${MONTH_ABBR[mo - 1]} '${String(y).slice(2)}`,
      revenue: entry.revenue,
      cost: entry.cost,
      profit,
      marginPct,
      billableHours: entry.billableHours,
      effBillingRate,
    };
  });
}

// ─── Export ───────────────────────────────────────────────────────────────────
export function buildExportRows(drillDown: Record<number, AMDrillEntry>): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];
  Object.values(drillDown)
    .sort((a, b) => b.revenue - a.revenue)
    .forEach(am => {
      Object.values(am.clients)
        .sort((a, b) => a.clientName.localeCompare(b.clientName))
        .forEach(cl => {
          Object.values(cl.projects)
            .sort((a, b) => a.projectName.localeCompare(b.projectName))
            .forEach(proj => {
              Object.values(proj.resources)
                .sort((a, b) => a.resourceName.localeCompare(b.resourceName))
                .forEach(res => {
                  const util = res.total > 0 ? Math.min(100, (res.billable / res.total) * 100) : 0;
                  rows.push({
                    'Account Owner': am.amName,
                    'Client': cl.clientName,
                    'Project': proj.projectName,
                    'PM': proj.projectManager,
                    'Resource': res.resourceName,
                    'Billable hrs': Math.round(res.billable * 100) / 100,
                    'Total hrs': Math.round(res.total * 100) / 100,
                    'Util %': Math.round(util * 10) / 10,
                    'Revenue ($)': Math.round(res.revenue),
                    'Cost ($)': Math.round(res.cost),
                    'DRC ($)': Math.round(res.directResourceCost),
                    'Margin ($)': Math.round(res.profit),
                  });
                });
            });
        });
    });
  return rows;
}
